import { c as _c } from "react/compiler-runtime";
import { Box } from "@mui/material";
import React, { useRef } from "react";
import { type OrbitControls as ThreeOrbitControls } from "three-stdlib";
import { useHeroOrbitModelMotion } from "#/features/homePage/hooks/useHeroOrbitModelMotion";
import { useLandingDecor3dMobileEntrance } from "#/features/homePage/hooks/useLandingDecor3dMobileEntrance";
import { LANDING_DECOR_GLASS_OPACITY_SX, LANDING_DECOR_MODEL_CAMERA, LANDING_DECOR_MODEL_DISTANCE, LANDING_DECOR_MODEL_FOV, LANDING_DECOR_MODEL_TARGET, LANDING_DECOR_PYTHON_BASE_AZIMUTH, LANDING_DECOR_PYTHON_BASE_POLAR, LANDING_DECOR_PYTHON_CANVAS_SIZE_PX, LANDING_DECOR_PYTHON_IDLE_MOTION_PHASE_SEC } from "#/features/homePage/lib/landingDecor3dConstants";
import { PythonLogoModelView } from "#/shared/ui/molecules/PythonLogoModelView";

/**
 * Ambient Python 3D logo: absolutely positioned within a `position: relative` parent
 * so it does not take foreground layout space (same idea as the hero brand model).
 */
export const HomeLandingPythonDecor: React.FC = () => {
  const $ = _c(9);
  const pythonControlsRef = useRef(null);
  const pythonVisibilityAnchorRef = useRef(null);
  const {
    mobileEntranceSx
  } = useLandingDecor3dMobileEntrance("python", pythonVisibilityAnchorRef);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      controlsRef: pythonControlsRef,
      baseAzimuth: LANDING_DECOR_PYTHON_BASE_AZIMUTH,
      basePolar: LANDING_DECOR_PYTHON_BASE_POLAR,
      invertPointerX: true,
      idleMotionPhaseSec: LANDING_DECOR_PYTHON_IDLE_MOTION_PHASE_SEC
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useHeroOrbitModelMotion(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      position: "absolute",
      inset: 0,
      overflow: "visible",
      pointerEvents: "none",
      zIndex: 0
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      position: "absolute",
      top: "50%",
      left: {
        xs: -118,
        sm: -180,
        md: -320
      },
      transform: "translateY(-50%)",
      width: LANDING_DECOR_PYTHON_CANVAS_SIZE_PX,
      height: LANDING_DECOR_PYTHON_CANVAS_SIZE_PX
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      width: "100%",
      height: "100%",
      opacity: LANDING_DECOR_GLASS_OPACITY_SX,
      filter: "blur(0.4px)",
      maskImage: "radial-gradient(circle at 52% 50%, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.88) 30%, rgba(0,0,0,0.44) 64%, transparent 90%)"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== mobileEntranceSx) {
    t4 = [t3, mobileEntranceSx];
    $[4] = mobileEntranceSx;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PythonLogoModelView controlsRef={pythonControlsRef} interactive={false} cameraPosition={LANDING_DECOR_MODEL_CAMERA} cameraFov={LANDING_DECOR_MODEL_FOV} target={LANDING_DECOR_MODEL_TARGET} distanceRange={LANDING_DECOR_MODEL_DISTANCE} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <Box ref={pythonVisibilityAnchorRef} aria-hidden={true} sx={t1}><Box sx={t2}><Box sx={t4}>{t5}</Box></Box></Box>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};